import React, { useState, useEffect } from 'react';
import AssignmentList from './AssignmentList';
import CreateAssignment from './CreateAssignment';
import AssignmentDetailSidebar from './AssignmentDetailSidebar';
import RubricBuilder from './RubricBuilder';
import GradingDashboard from './GradingDashboard';
import GradingView from './GradingView';
import StudentGradeView from './StudentGradeView';
import { useClass } from '../../utils/ClassContext';
import { hasInstructorPermissions } from '../../utils/permissions';

const Assignments = ({ userRole, username, currentPage, onNavigate }) => {
  const { selectedClass } = useClass();
  const isInstructor = hasInstructorPermissions(userRole);
  const [currentView, setCurrentView] = useState('list'); // 'list', 'rubric-builder', 'grading-dashboard', 'grading-view', 'student-grade-view'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [previousPage, setPreviousPage] = useState(currentPage);

  // Sidebar states
  const [showDetailSidebar, setShowDetailSidebar] = useState(false);

  // Reset to list view when navigating back to assignments page
  useEffect(() => {
    // Check if we just navigated TO the assignments page from somewhere else
    if (currentPage === 'assignments' && previousPage !== 'assignments') {
      setCurrentView('list');
      setSelectedAssignment(null);
      setShowDetailSidebar(false);
      setShowCreateModal(false);
    }
    setPreviousPage(currentPage);
  }, [currentPage, previousPage]);

  const handleAssignmentCreated = (newAssignment) => {
    console.log('New assignment created:', newAssignment);
    setShowCreateModal(false);
    setRefreshTrigger(prev => prev + 1);
    // Redirect to rubric builder for the new assignment
    if (newAssignment) {
      setSelectedAssignment(newAssignment);
      setCurrentView('rubric-builder');
    }
  };

  const handleSelectAssignment = (assignment) => {
    console.log('Selected assignment:', assignment);
    setSelectedAssignment(assignment);
    if (isInstructor) {
      // Show detail sidebar for instructors
      setShowDetailSidebar(true);
    } else {
      // Students: navigate to the first component's editor, or document editor as fallback
      if (onNavigate) {
        const components = assignment.components || [];
        if (components.length > 0) {
          const first = components.sort((a, b) => a.order - b.order)[0];
          const navData = {
            assignmentId: assignment.assignment_id,
            componentId: first.component_id
          };
          switch (first.type) {
            case 'erd':
              navData.environmentId = first.content_ref?.requirement_id;
              onNavigate('editor', navData);
              break;
            case 'erd_env': {
              const firstEnvId = first.content_ref?.all_environments
                ? (first.content_ref?.environment_ids?.[0] || null)
                : first.content_ref?.environment_id;
              navData.environmentId = firstEnvId;
              onNavigate('editor', navData);
              break;
            }
            case 'code':
              navData.projectId = first.content_ref?.project_id;
              navData.fileId = first.content_ref?.file_id;
              onNavigate('code-editor', navData);
              break;
            default:
              navData.documentId = first.content_ref?.document_id || assignment.document_id;
              onNavigate('document-editor', navData);
          }
        } else {
          // Legacy assignment without components
          onNavigate('document-editor', {
            assignmentId: assignment.assignment_id,
            documentId: assignment.document_id
          });
        }
      }
    }
  };

  const handleCloseSidebar = () => {
    setShowDetailSidebar(false);
  };

  const handleAssignmentUpdated = (updatedAssignment) => {
    console.log('Assignment updated:', updatedAssignment);
    setSelectedAssignment(updatedAssignment);
    // Trigger refresh of assignment list
    setRefreshTrigger(prev => prev + 1);
  };

  const handleAssignmentDeleted = (assignmentId) => {
    console.log('Assignment deleted:', assignmentId);
    setSelectedAssignment(null);
    setShowDetailSidebar(false);
    // Trigger refresh of assignment list
    setRefreshTrigger(prev => prev + 1);
  };

  const handleNavigateToEditor = () => {
    setShowDetailSidebar(false);
    // Navigate to document editor with assignment context
    if (onNavigate && selectedAssignment) {
      onNavigate('document-editor', {
        assignmentId: selectedAssignment.assignment_id,
        documentId: selectedAssignment.document_id
      });
    }
  };

  const handleOpenComponent = (component) => {
    setShowDetailSidebar(false);
    if (!onNavigate || !selectedAssignment) return;

    const navData = {
      assignmentId: selectedAssignment.assignment_id,
      componentId: component.component_id
    };

    // Navigate to the actual editor page with assignment context
    switch (component.type) {
      case 'document':
        navData.documentId = component.content_ref?.document_id;
        onNavigate('document-editor', navData);
        break;
      case 'erd':
        navData.environmentId = component.content_ref?.requirement_id;
        onNavigate('editor', navData); // 'editor' = ERD Editor page ID
        break;
      case 'erd_env': {
        const envId = component.content_ref?.all_environments
          ? (component.content_ref?.environment_ids?.[0] || null)
          : component.content_ref?.environment_id;
        navData.environmentId = envId;
        onNavigate('editor', navData);
        break;
      }
      case 'code':
        navData.projectId = component.content_ref?.project_id;
        navData.fileId = component.content_ref?.file_id;
        onNavigate('code-editor', navData);
        break;
      default:
        onNavigate('document-editor', navData);
    }
  };

  const handleOpenRubric = () => {
    setShowDetailSidebar(false);
    setCurrentView('rubric-builder');
  };

  const handleOpenGrading = () => {
    setShowDetailSidebar(false);
    setCurrentView('grading-dashboard');
  };

  const handleGradeSubmission = (submission, submissions = []) => {
    setSelectedSubmission(submission);
    setAllSubmissions(submissions);
    setCurrentView('grading-view');
  };

  // Get class context for API calls
  const classContext = selectedClass ? {
    class_id: selectedClass.class_id,
    class_code: selectedClass.class_code,
    username: username
  } : null;

  // Render based on current view
  const handleCloseRubricBuilder = () => {
    setCurrentView('list');
  };

  const renderView = () => {
    switch (currentView) {
      case 'rubric-builder':
        return (
          <RubricBuilder
            assignment={selectedAssignment}
            classContext={classContext}
            onBack={handleCloseRubricBuilder}
            onNavigate={onNavigate}
          />
        );

      case 'grading-dashboard':
        return (
          <GradingDashboard
            assignment={selectedAssignment}
            classContext={classContext}
            onBack={() => setCurrentView('list')}
            onGradeSubmission={(sub, subs) => handleGradeSubmission(sub, subs || [])}
          />
        );

      case 'grading-view':
        return (
          <GradingView
            submission={selectedSubmission}
            assignment={selectedAssignment}
            classContext={classContext}
            submissions={allSubmissions}
            onBack={() => setCurrentView('grading-dashboard')}
            onNavigate={(sub) => setSelectedSubmission(sub)}
          />
        );

      case 'student-grade-view':
        return (
          <StudentGradeView
            assignment={selectedAssignment}
            classContext={classContext}
            onBack={() => setCurrentView('list')}
          />
        );

      case 'list':
      default:
        return (
          <>
            <AssignmentList
              key={refreshTrigger}
              classContext={classContext}
              userRole={userRole}
              onSelectAssignment={handleSelectAssignment}
              onCreateNew={() => setShowCreateModal(true)}
              onNavigate={onNavigate}
              onViewGrade={(assignment) => {
                setSelectedAssignment(assignment);
                setCurrentView('student-grade-view');
              }}
            />

            {showCreateModal && (
              <CreateAssignment
                classContext={classContext}
                onClose={() => setShowCreateModal(false)}
                onCreated={handleAssignmentCreated}
              />
            )}

            {showDetailSidebar && selectedAssignment && (
              <AssignmentDetailSidebar
                assignment={selectedAssignment}
                classContext={classContext}
                onClose={handleCloseSidebar}
                onUpdate={handleAssignmentUpdated}
                onDelete={handleAssignmentDeleted}
                onOpenRubric={handleOpenRubric}
                onBuildAssignment={handleNavigateToEditor}
                onOpenComponent={handleOpenComponent}
                onGradeSubmissions={handleOpenGrading}
                onNavigate={onNavigate}
              />
            )}
          </>
        );
    }
  };

  return (
    <div className="h-full bg-white flex flex-col">
      {renderView()}
    </div>
  );
};

export default Assignments;
